
import React from 'react';
import { Link } from 'react-router-dom';
import { stats } from '../mockData';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Award, Users } from 'lucide-react';
import Team from '../components/Team';

const AboutUs = () => {
  return (
    <div className="pt-20">
      {/* Hero Section with Background */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-white">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2000"
            alt="About Us"
            className="w-full h-full object-cover animate-ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in-up" style={{ fontFamily: "'Playfair Display', serif" }}>
              About Bhagat Prop Built
            </h1>
            <div className="flex items-center justify-center gap-4 mb-6 animate-fade-in">
              <div className="h-1 w-20 bg-[#B8944A] rounded"></div>
              <div className="w-3 h-3 bg-[#B8944A] rounded-full"></div>
              <div className="h-1 w-20 bg-[#B8944A] rounded"></div>
            </div>
            <p className="text-xl text-white/90 leading-relaxed animate-fade-in-up animation-delay-200">
              Building Trust, Creating Excellence
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Founded by <strong>Mr. Hari Prakash Bhati</strong> with a vision to revolutionize the real estate industry, Bhagat Prop Built has grown from a small consultancy to one of the most respected names in premium property services. Our journey is built on trust, transparency, and an unwavering commitment to client satisfaction.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We believe that every property transaction is more than just a business deal—it's about understanding dreams, aspirations, and creating spaces where memories are made. This philosophy drives everything we do.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Today, with countless satisfied clients, we continue to set new standards in the real estate industry.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa"
                alt="Our Story"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-b from-white to-amber-50/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide our every decision and interaction
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-[#B8944A] transition-all hover:shadow-lg">
              <div className="w-20 h-20 bg-[#B8944A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                We strive for excellence in every transaction, ensuring the highest standards of service and professionalism.
              </p>
            </div>

            <div className="text-center bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-[#B8944A] transition-all hover:shadow-lg">
              <div className="w-20 h-20 bg-[#B8944A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Integrity</h3>
              <p className="text-gray-600 leading-relaxed">
                Trust and transparency form the foundation of our relationships with clients, partners, and stakeholders.
              </p>
            </div>

            <div className="text-center bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-[#B8944A] transition-all hover:shadow-lg">
              <div className="w-20 h-20 bg-[#B8944A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Client-Centric</h3>
              <p className="text-gray-600 leading-relaxed">
                Your dreams and goals are our priority. We go above and beyond to exceed expectations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#B8944A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <p className="text-5xl font-bold mb-2">{stat.number}</p>
                <p className="text-lg font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Have Sold */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Have Sold
            </h2>
            <p className="text-xl text-gray-600">
              Our Track Record of Successful Land Transactions
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-8">
            {/* OMAXE */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border-l-4 border-[#B8944A] animate-fade-in-up animate-delay-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">OMAXE</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Agra</p>
                  <p className="text-[#B8944A] font-bold text-xl">70 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Vrindavan</p>
                  <p className="text-[#B8944A] font-bold text-xl">60 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Palwal</p>
                  <p className="text-[#B8944A] font-bold text-xl">100 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Sonipat</p>
                  <p className="text-[#B8944A] font-bold text-xl">400 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Jaipur</p>
                  <p className="text-[#B8944A] font-bold text-xl">450 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Ajmer</p>
                  <p className="text-[#B8944A] font-bold text-xl">60 Acre</p>
                </div>
              </div>
            </div>

            {/* SUNCITY */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border-l-4 border-[#B8944A] animate-fade-in-up animate-delay-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">SUNCITY</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Kethal, Haryana</p>
                  <p className="text-[#B8944A] font-bold text-xl">25 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">JLN Marg Jaipur (Joint Venture)</p>
                  <p className="text-[#B8944A] font-bold text-xl">29 Acre</p>
                  <p className="text-gray-500 text-xs mt-1">with Jewels India</p>
                </div>
              </div>
            </div>

            {/* WAVE CITY */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border-l-4 border-[#B8944A] animate-fade-in-up animate-delay-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">WAVE CITY</h3>
              <div className="bg-white rounded-lg p-4 shadow-sm inline-block">
                <p className="text-[#B8944A] font-bold text-xl">70 Acre Land</p>
              </div>
            </div>

            {/* LODHA GROUP */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border-l-4 border-[#B8944A] animate-fade-in-up animate-delay-400">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">LODHA GROUP</h3>
              <p className="text-gray-600 mb-4 italic">(The House of Abhinandan Lodha)</p>
              <div className="bg-white rounded-lg p-4 shadow-sm inline-block">
                <p className="text-gray-600 text-sm">Vrindavan</p>
                <p className="text-[#B8944A] font-bold text-xl">70 Acre</p>
              </div>
            </div>

            {/* STERLING VAIDIC BLAZE */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border-l-4 border-[#B8944A] animate-fade-in-up animate-delay-450">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">STERLING VAIDIC BLAZE</h3>
              <div className="bg-white rounded-lg p-4 shadow-sm inline-block">
                <p className="text-gray-600 text-sm">Haryana</p>
                <p className="text-[#B8944A] font-bold text-xl">70 Acre</p>
                <p className="text-gray-500 text-xs mt-1">(Non Film City - Nagliya Group)</p>
              </div>
            </div>

            {/* Other Major Projects */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-8 border-l-4 border-[#B8944A] animate-fade-in-up animate-delay-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Other Major Projects</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Satyam Shivam Sundaram - Hotel Land, Ayodhya</p>
                  <p className="text-[#B8944A] font-bold text-xl">10 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">R Jhon Residential - Villa</p>
                  <p className="text-[#B8944A] font-bold text-xl">25 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Satyam Shivam Sundaram, Haryana</p>
                  <p className="text-[#B8944A] font-bold text-xl">25 Acre</p>
                  <p className="text-gray-500 text-xs mt-1">(Near Jewar Airport 2km)</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Numax, Muzaffarnagar</p>
                  <p className="text-[#B8944A] font-bold text-xl">100 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Vikram Overseas, Palwal</p>
                  <p className="text-[#B8944A] font-bold text-xl">262 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Pearlpet, Palwal Hodal (Mathura Road)</p>
                  <p className="text-[#B8944A] font-bold text-xl">65 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Chomuha, Ajhai, Mathura</p>
                  <p className="text-[#B8944A] font-bold text-xl">250 Acre</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-600 text-sm">Dhouj, Haryana</p>
                  <p className="text-[#B8944A] font-bold text-xl">55 Acre</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <Team />

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Find Your Dream Property?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let our expert team guide you through your real estate journey
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-[#B8944A] hover:bg-[#A07D3A] text-white px-8 py-6 text-base font-medium rounded-full transition-all duration-300"
            >
              Contact Us Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
